import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useId } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { ConvertibleSVG, CoupeSVG, ElectricSVG, HatchBackSVG, SUVSVG, SedanSVG, TruckSVG, VanSVG, WagonSVG } from '../FilterSVGs';
import type { BodyStyleFilterProps } from '@/types/searchSide';
interface BodyStyleOption {
  value: string;
  label: string;
  icon: React.ComponentType;
  relatedStyles?: string[];
}
const bodyStyleOptions: BodyStyleOption[] = [{
  value: 'Sedan',
  label: 'Sedan',
  icon: SedanSVG
}, {
  value: 'SUV',
  label: 'SUVs',
  icon: SUVSVG,
  relatedStyles: ['Hatchback', 'Wagon']
}, {
  value: 'Truck',
  label: 'Trucks',
  icon: TruckSVG
}, {
  value: 'Van',
  label: 'Van',
  icon: VanSVG
}, {
  value: 'Convertible',
  label: 'Convertible',
  icon: ConvertibleSVG
}, {
  value: 'Coupe',
  label: 'Coupe',
  icon: CoupeSVG
}, {
  value: 'Hatchback',
  label: 'HatchBack',
  icon: HatchBackSVG,
  relatedStyles: ['SUV', 'Wagon']
}, {
  value: 'Wagon',
  label: 'Wagon',
  icon: WagonSVG,
  relatedStyles: ['SUV', 'Hatchback']
}, {
  value: 'Electric',
  label: 'Electric',
  icon: ElectricSVG
}];
const ClearButton: React.FC<{
  onClear: () => void;
  count: number;
}> = t0 => {
  const $ = _c(4);
  const {
    onClear,
    count
  } = t0;
  const t1 = `Clear ${count} selected body styles`;
  let t2;
  if ($[0] !== count || $[1] !== onClear || $[2] !== t1) {
    t2 = <button type="button" className="t-text-xs t-font-semibold t-text-automotive-primary hover:t-text-automotive-secondary t-transition-colors focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 t-rounded" onClick={onClear} aria-label={t1}>Clear {count}</button>;
    $[0] = count;
    $[1] = onClear;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export const BodyStyleFilter: React.FC<BodyStyleFilterProps & {
  isOpen: boolean;
  onToggle: () => void;
}> = t0 => {
  const $ = _c(42);
  const {
    availableBodyStyles,
    selectedBodyStyles,
    onBodyStyleChange,
    onClear,
    isOpen,
    onToggle,
    className: t1,
    "aria-label": ariaLabel
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const sectionId = useId();
  const triggerId = `body-style-${sectionId}-trigger`;
  const optionsId = `body-style-${sectionId}-options`;
  let t2;
  if ($[0] !== onBodyStyleChange || $[1] !== selectedBodyStyles) {
    t2 = (styleValue, relatedStyles) => {
      const isSelected = selectedBodyStyles.includes(styleValue);
      if (isSelected) {
        const newStyles = selectedBodyStyles.filter(style => style !== styleValue);
        onBodyStyleChange(newStyles);
      } else {
        const newStyles_0 = [...selectedBodyStyles, styleValue];
        if (relatedStyles) {
          relatedStyles.forEach(relatedStyle => {
            if (!newStyles_0.includes(relatedStyle)) {
              newStyles_0.push(relatedStyle);
            }
          });
        }
        onBodyStyleChange(newStyles_0);
      }
    };
    $[0] = onBodyStyleChange;
    $[1] = selectedBodyStyles;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleStyleSelection = t2;
  const selectedCount = selectedBodyStyles.length;
  let t3;
  if ($[3] !== availableBodyStyles) {
    t3 = new Set(availableBodyStyles?.map(_temp));
    $[3] = availableBodyStyles;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const normalizedAvailableStyles = t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[5] !== ariaLabel || $[6] !== className || $[7] !== handleStyleSelection || $[8] !== isOpen || $[9] !== normalizedAvailableStyles || $[10] !== onClear || $[11] !== onToggle || $[12] !== optionsId || $[13] !== selectedBodyStyles || $[14] !== selectedCount || $[15] !== triggerId) {
    const visibleBodyStyleOptions = normalizedAvailableStyles.size ? bodyStyleOptions.filter(option => normalizedAvailableStyles.has(option.value.toLowerCase())) : bodyStyleOptions;
    t6 = className;
    t7 = ariaLabel;
    let t8;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span>Body Style</span>;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    const t9 = isOpen ? faAngleUp : faAngleDown;
    let t10;
    if ($[21] !== t9) {
      t10 = <FontAwesomeIcon icon={t9} className="t-h-4 t-w-4 t-text-[#928c96]" aria-hidden="true" />;
      $[21] = t9;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== isOpen || $[24] !== onToggle || $[25] !== optionsId || $[26] !== t10 || $[27] !== triggerId) {
      t11 = <button id={triggerId} className="t-flex t-w-full t-items-center t-justify-between t-text-left t-text-sm t-font-extrabold t-text-[#38323d] t-transition-colors hover:t-text-brand-violet focus:t-rounded focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2" type="button" onClick={onToggle} aria-expanded={isOpen} aria-controls={optionsId}>{t8}{t10}</button>;
      $[23] = isOpen;
      $[24] = onToggle;
      $[25] = optionsId;
      $[26] = t10;
      $[27] = triggerId;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== onClear || $[30] !== selectedCount) {
      t12 = selectedCount > 0 && <div className="t-mt-2"><ClearButton onClear={onClear} count={selectedCount} /></div>;
      $[29] = onClear;
      $[30] = selectedCount;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    if ($[32] !== t11 || $[33] !== t12) {
      t4 = <div>{t11}{t12}</div>;
      $[32] = t11;
      $[33] = t12;
      $[34] = t4;
    } else {
      t4 = $[34];
    }
    t5 = isOpen && <div id={optionsId} className="t-mt-4 t-grid t-gap-1" role="group" aria-labelledby={triggerId}>{visibleBodyStyleOptions.map(option_0 => {
        const isSelected_0 = selectedBodyStyles.includes(option_0.value);
        return <button key={option_0.value} type="button" className={`t-flex t-items-center t-gap-2.5 t-rounded-md t-px-1 t-py-2 t-text-left t-transition-colors t-duration-200 ${isSelected_0 ? "t-bg-[#f7f3ff]" : "t-bg-white hover:t-bg-[#faf9fb]"}`} onClick={() => handleStyleSelection(option_0.value, option_0.relatedStyles)} aria-pressed={isSelected_0} aria-label={`${option_0.label} body style ${isSelected_0 ? "selected" : "not selected"}`}><span className={`t-flex t-h-4 t-w-4 t-flex-shrink-0 t-items-center t-justify-center t-rounded t-border ${isSelected_0 ? "t-border-brand-violet t-bg-brand-violet t-text-white" : "t-border-[#cfcad2] t-bg-white"}`} aria-hidden="true">{isSelected_0 && <span className="t-text-[10px] t-font-black">✓</span>}</span><span className={`t-text-[13px] t-font-medium ${isSelected_0 ? "t-text-brand-violet-dark" : "t-text-gray-700"}`}>{option_0.label}</span></button>;
      })}</div>;
    $[5] = ariaLabel;
    $[6] = className;
    $[7] = handleStyleSelection;
    $[8] = isOpen;
    $[9] = normalizedAvailableStyles;
    $[10] = onClear;
    $[11] = onToggle;
    $[12] = optionsId;
    $[13] = selectedBodyStyles;
    $[14] = selectedCount;
    $[15] = triggerId;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
  }
  let t8;
  if ($[35] !== t4 || $[36] !== t5) {
    t8 = <div>{t4}{t5}</div>;
    $[35] = t4;
    $[36] = t5;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] !== t6 || $[39] !== t7 || $[40] !== t8) {
    t9 = <div className={t6} aria-label={t7}>{t8}</div>;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  return t9;
};
function _temp(style_0) {
  return style_0.toLowerCase();
}